import { ReparoApi } from './index';

export type CreateConsultantInviteParams = {
    email: string;
    firstName?: string;
    lastName?: string;
    resourceKeys: string[];
    columnVisibility?: Record<string, string[]>;
};

export type ConsultantInviteResponse = {
    inviteId?: string;
    email: string;
    firstName?: string | null;
    lastName?: string | null;
    merchantId?: string;
    expiresAt?: string;
    isUsed: boolean;
    link?: string | null;
};

/**
 * Response from sharing consultant access.
 * existingUser: true → Access granted to existing consultant (no link needed)
 * existingUser: false → New invite created (link provided)
 */
export type ShareConsultantAccessResponse = {
    existingUser: boolean;
    email: string;
    firstName?: string | null;
    lastName?: string | null;
    userId?: string | null;
    inviteId?: string | null;
    link?: string | null;
    resourceKeys: string[];
    message?: string | null;
    /**
     * True only when the invitation email actually reached the mail provider. False covers a
     * failed send and an environment with no mailer configured, so the link stays the
     * fallback rather than the UI claiming a delivery that did not happen.
     */
    emailed?: boolean;
};

export type LookupConsultantResponse = {
    exists: boolean;
    firstName?: string | null;
    lastName?: string | null;
};

export type AcceptConsultantInviteParams = {
    token: string;
    password: string;
    firstName?: string;
    lastName?: string;
};

export type LoginPayloadResponse = {
    user: {
        id?: string;
        email?: string | null;
        firstName?: string | null;
        lastName?: string | null;
        organizationId?: string | null;
        role?: string;
    };
    accessToken: {
        token: string;
        expiresIn: number;
    };
};

export const createConsultantInvite = (
    dto: CreateConsultantInviteParams,
): Promise<ShareConsultantAccessResponse> =>
    ReparoApi({
        method: 'POST',
        url: '/consultant-invites',
        body: JSON.stringify(dto),
    }) as unknown as Promise<ShareConsultantAccessResponse>;

export const lookupConsultant = (
    email: string,
): Promise<LookupConsultantResponse> =>
    ReparoApi({
        method: 'GET',
        url: `/consultant-invites/lookup/${encodeURIComponent(email)}`,
        body: undefined,
    }) as unknown as Promise<LookupConsultantResponse>;

export const getConsultantInvite = (
    token: string,
): Promise<ConsultantInviteResponse> =>
    ReparoApi({
        method: 'GET',
        url: `/consultant-invites/${token}`,
        body: undefined,
    }) as unknown as Promise<ConsultantInviteResponse>;

export const acceptConsultantInvite = (
    dto: AcceptConsultantInviteParams,
): Promise<LoginPayloadResponse> =>
    ReparoApi({
        method: 'POST',
        url: '/consultant-invites/accept',
        body: JSON.stringify(dto),
    }) as unknown as Promise<LoginPayloadResponse>;
